import { useIsClient, useIsomorphicLayoutEffect } from 'usehooks-ts';

export interface IsomorphicStylesProps {
  /**
   * Starting index for the identifiers set on the style elements. This is used
   * server-side to where styles are streamed in batches.
   */
  idStart?: number;
  /**
   * Indicates that the client ran into an error during hydration but is able to
   * recover. The server styles need to be retained when this happens.
   */
  isClientRecovery?: boolean;
  /**
   * List of CSS strings to be rendered as style elements.
   */
  styles: string[];
}

/**
 * Renders a list of style elements for the provided CSS strings. Enables the
 * initial styles gathered on the server to be included in the response. On the
 * client, matching styles will ensure proper hydration on the first render,
 * after which they are removed.
 */
export const IsomorphicStyles = ({
  styles,
  idStart = 0,
  isClientRecovery = false,
}: IsomorphicStylesProps) => {
  // Returns true after the first render on the client
  const isClient = useIsClient();

  useIsomorphicLayoutEffect(() => {
    // Server styles can be removed after the client is fully hydrated, unless
    // the client is recovering from an error - Then we need to keep the styles
    if (!isClient || isClientRecovery) return;

    const elements = document.querySelectorAll(`[data-isomorphic-styles]`);

    for (const style of elements) {
      style.remove();
    }
  }, [isClient, isClientRecovery]);

  return (
    <>
      {styles.map((__html, index) => (
        <style
          // eslint-disable-next-line react/no-array-index-key -- Index is okay here
          key={idStart + index}
          // eslint-disable-next-line react/no-danger -- Alas, danger is required
          dangerouslySetInnerHTML={{ __html }}
          data-isomorphic-styles={idStart + index}
        />
      ))}
    </>
  );
};
